/**
 * ClipboardService - 클립보드 작업을 처리하는 서비스
 * Single Responsibility: 클립보드 읽기/쓰기 작업만 담당
 */

export class ClipboardService {
  /**
   * 클립보드에서 텍스트 읽기
   * Electron과 Web 환경 모두 지원
   */
  static async readText(): Promise<string> {
    try {
      // Electron 환경 체크
      if (typeof window !== 'undefined' && (window as any).electronAPI?.clipboard) {
        const result = await (window as any).electronAPI.clipboard.readText()
        return result || ''
      }
      
      // Web 환경
      if (navigator.clipboard && navigator.clipboard.readText) {
        const text = await navigator.clipboard.readText()
        return text || ''
      }
      
      // Fallback
      console.warn('[ClipboardService] Clipboard API not available')
      return ''
    } catch (error) {
      console.error('[ClipboardService] Failed to read clipboard:', error)
      return ''
    }
  }

  /**
   * 클립보드에 텍스트 쓰기
   * Electron과 Web 환경 모두 지원
   */
  static async writeText(text: string): Promise<boolean> {
    try {
      // 빈 텍스트 체크
      if (!text) {
        console.warn('[ClipboardService] Empty text provided')
        return false
      }

      // Electron 환경 체크
      if (typeof window !== 'undefined' && (window as any).electronAPI?.clipboard) {
        const result = await (window as any).electronAPI.clipboard.writeText(text)
        return result?.success !== false
      }

      // Web 환경
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text)
        return true
      }

      // Fallback
      console.warn('[ClipboardService] Clipboard API not available')
      return false
    } catch (error) {
      console.error('[ClipboardService] Failed to write to clipboard:', error)
      return false
    }
  }

  /**
   * 클립보드에 HTML과 텍스트 동시에 쓰기 (리치 포맷)
   * Electron과 Web 환경 모두 지원
   */
  static async writeHTML(html: string, text: string): Promise<boolean> {
    try {
      // 빈 HTML 또는 텍스트 체크
      if (!html || !text) {
        console.warn('[ClipboardService] Empty HTML or text provided')
        return false
      }

      // Electron 환경 체크
      if (typeof window !== 'undefined' && (window as any).electronAPI?.clipboard?.writeHTML) {
        const result = await (window as any).electronAPI.clipboard.writeHTML(html, text)
        return result?.success !== false
      }

      // Web 환경 - Clipboard API 사용
      if (navigator.clipboard && navigator.clipboard.write) {
        const htmlBlob = new Blob([html], { type: 'text/html' })
        const textBlob = new Blob([text], { type: 'text/plain' })
        const clipboardItem = new ClipboardItem({
          'text/html': htmlBlob,
          'text/plain': textBlob
        })
        await navigator.clipboard.write([clipboardItem])
        return true
      }

      // Fallback - 텍스트만 복사
      console.warn('[ClipboardService] Rich format not available, falling back to text only')
      return await this.writeText(text)
    } catch (error) {
      console.error('[ClipboardService] Failed to write HTML to clipboard:', error)
      // Fallback - 텍스트만 복사
      return await this.writeText(text)
    }
  }

  /**
   * 클립보드 API 사용 가능 여부 확인
   */
  static isAvailable(): boolean {
    // Electron 환경
    if (typeof window !== 'undefined' && (window as any).electronAPI?.clipboard) {
      return true
    }

    // Web 환경
    if (navigator.clipboard) {
      return true
    }

    return false
  }
}